import { useEffect, useState } from 'react';
import { useMarkets } from '@/state/markets';
import { getTicker } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { priceFmt } from '@/lib/format';
import { seedFor } from '@/engine/clock';
import { prngFromSeed } from '@/engine/prng';
import { cn } from '@/lib/cn';

const ROW_BUCKET_MS = 1000;
const ROWS = 12;

interface Print {
  t: number;
  price: number;
  size: number;
  side: 'buy' | 'sell';
}

function generatePrints(tickerId: string, now: number): Print[] {
  const cfg = getTicker(tickerId);
  if (!cfg) return [];
  const out: Print[] = [];
  for (let i = 0; i < ROWS; i++) {
    const t = now - i * ROW_BUCKET_MS;
    const seed = seedFor(`prints:${tickerId}`, t, ROW_BUCKET_MS);
    const rng = prngFromSeed(seed);
    const livePrice = priceAt(cfg, t);
    const printJitter = (rng() - 0.5) * 0.001;
    out.push({
      t,
      price: livePrice * (1 + printJitter),
      size: Math.floor(rng() * 500 + 25),
      side: rng() > 0.5 ? 'buy' : 'sell',
    });
  }
  return out;
}

export function TimeAndSales() {
  const tickerId = useMarkets((s) => s.activeTickerId);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const prints = generatePrints(tickerId, now);
  const maxSize = Math.max(...prints.map((p) => p.size), 1);

  return (
    <div className="flex h-full flex-col bg-[var(--bg-surface)] text-mono-xs">
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-3 py-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
        <span>Time & Sales</span>
        <span>{tickerId}</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {prints.map((p, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center justify-between px-3 py-0.5 tabular-nums',
              i === 0 && p.size > maxSize * 0.7 && 'animate-[flash_400ms_ease-out_1]',
            )}
          >
            <span className="text-[var(--text-muted)]">{fmtTime(p.t)}</span>
            <span style={{ color: p.side === 'buy' ? 'var(--positive)' : 'var(--negative)' }}>
              {priceFmt(p.price)}
            </span>
            <span className="text-[var(--text-secondary)]">{p.size}</span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes flash {
          0% { background-color: rgba(255, 184, 77, 0.3); }
          100% { background-color: transparent; }
        }
      `}</style>
    </div>
  );
}

function fmtTime(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}
